import {Component, Input, OnInit} from '@angular/core';
import {ModalConfig} from '../../../model/modal-config';
import {BaseActions} from '../../../shared/base-actions';
import {Order} from '../../../model/order';
import {PageNames} from '../../../shared/page-names.enum';
import {NavigationService} from '../../../services/navigation/navigation.service';
import {NavigationState} from '../../../model/navigation/navigation-state';
import {EmitterConstants} from '../../../shared/emitter-constants';
import {Emitter} from '../../../shared/utils/emitter/emitter';
import {takeUntil} from 'rxjs/operators';
import {Trial} from '../../../model/trial';
import {Subscription} from 'rxjs';
import {AnalyticsService} from '../../../services/analytics/analytics.service';
import {OmapiIdConstants} from '../../../shared/omapi-id-constants';

@Component({
  selector: 'app-trial-modal-manager',
  templateUrl: './trial-modal-manager.component.html',
  styleUrls: ['./trial-modal-manager.component.less']
})
export class TrialModalManagerComponent extends BaseActions implements OnInit {
  modalConfig = new ModalConfig();
  navigationState = new NavigationState();
  previousPageState = new NavigationState();
  trialsToCompare = [];
  @Input()
  order: Order;
  @Input() runningTrials = [];
  previousPage: string;
  entryComponent: string;
  successOmapiId: string;
  trialOmapiId: string;
  error: Error;
  eventSubscriptions: Array<Subscription> = [];

  constructor(private navigationService: NavigationService, private analyticsService: AnalyticsService) {
    super();
  }

  ngOnInit() {
    this.subscribeToEvents();
  }

  private subscribeToEvents() {
    this.eventSubscriptions.push(Emitter.of(EmitterConstants.BACK_PRESSED).pipe(takeUntil(this.stopSubscription)).subscribe(backValue => this.handleOnBackValue(backValue)));
    this.eventSubscriptions.push(Emitter.of(EmitterConstants.OPEN_CONDITIONAL_MODAL).pipe(takeUntil(this.stopSubscription)).subscribe(value => this.openConditions(value)));
    this.eventSubscriptions.push(Emitter.of(EmitterConstants.OPEN_INPUT_TRIAL_CODE_MODAL).pipe(takeUntil(this.stopSubscription)).subscribe(value => this.openTrialActivationModal(value)));
    this.eventSubscriptions.push(Emitter.of(EmitterConstants.OPEN_TRIAL_COMPARISON_MODAL).pipe(takeUntil(this.stopSubscription)).subscribe((trials: Array<Trial>) => this.openTrialComparison(trials)));
    this.eventSubscriptions.push(Emitter.of(EmitterConstants.OPEN_TRIAL_SUCCESS_MODAL).pipe(takeUntil(this.stopSubscription)).subscribe(trial => this.openTrialSuccessModal(trial)));
    this.eventSubscriptions.push(Emitter.of(EmitterConstants.OPEN_TRIAL_MAX_ATTEMPTS_REACHED_MODAL).pipe(takeUntil(this.stopSubscription)).subscribe(x => this.showTrialMaxAttemptsReached()));
    this.eventSubscriptions.push(Emitter.of(EmitterConstants.OPEN_TRIAL_ACTIVATION_TIMEOUT).pipe(takeUntil(this.stopSubscription)).subscribe(value => this.openTrialActivationPendingModal(value)));

  }

  public openTrialSuccessModal(trial: Trial) {
    if (trial) {
      this.successOmapiId = trial.parentOmapiId;
      this.trialOmapiId = trial.omapiId;
      this.analyticsService.onVoucherCodeSuccess(this.trialOmapiId);
      this.showTrialSuccessModal();
    }
  }

  public openTrialComparison(trials: Array<Trial>) {
    if (trials && trials.length > 0) {
      this.trialsToCompare = trials;
      this.showTrialComparisonModal();
    }
  }

  public openTrialActivationModal(value): void {
    if (value) {
      this.previousPage = this.entryComponent = value;
      this.showTrialActivationModal();
    }
  }

  public handleOnBackValue(backValue): void {
    this.previousPage = backValue;
    if (backValue === PageNames.TRIAL_COMPARISON_MODAL) {
      this.showTrialActivationModal(backValue);
    } else if (backValue.componentName === PageNames.TRIAL_SUCCESS_MODAL) {
      if (this.entryComponent === PageNames.PLAY_SPORTS_PAGE
        && backValue.omapiId === OmapiIdConstants.PLAY_SPORTS_TRIAL) {
        this.removeDayPassAndTrialButtonsFromOrderOptions();
      }
      this.resetModalConfig(this.entryComponent, this.previousPageState);
    } else if (backValue !== this.entryComponent && this.entryComponent === PageNames.FIND_TRIAL_CODE_MODAL) {
      this.showFindTrialModal();
    } else if (backValue === PageNames.TRIAL_ATTEMPTS_REACHED) {
      this.showTrialActivationModal();
    } else if (this.entryComponent === PageNames.PLAY_SPORTS_PAGE) {
      this.resetModalConfig(PageNames.PLAY_SPORTS_PAGE, this.previousPageState);
    } else {
      this.resetModalConfig(PageNames.TRIALS_PAGE, this.previousPageState);
    }
  }

  private removeDayPassAndTrialButtonsFromOrderOptions() {
    const ORDER_OPTIONS_SECTION = 'productDetailOrderOptions';
    const lastNavItemIndex = this.previousPageState.navItemSource[ORDER_OPTIONS_SECTION][0].length - 1;
    this.previousPageState.navItemSource[ORDER_OPTIONS_SECTION][0] = this.previousPageState.navItemSource[ORDER_OPTIONS_SECTION][0].slice(0, lastNavItemIndex - 1);
    this.previousPageState.activeX = 0;
    this.previousPageState.navItemSource[ORDER_OPTIONS_SECTION][0][0].active = true;
  }

  openConditions(value): void {
    if (value) {
      this.modalConfig.showTrialCode = false;
      this.modalConfig.showOrderConditions = true;
      this.registerComponent(PageNames.CONDITIONS_MODAL);
    }
  }

  public openTrialActivationPendingModal(value: any): void {
    if (value) {
      if (value.component === PageNames.TRIAL_COMPARISON_MODAL) {
        this.modalConfig.showTrialComparison = false;
      } else if (value.component === PageNames.TRIAL_CODE_MODAL) {
        this.modalConfig.showTrialCode = false;
      }
      this.trialOmapiId = value.trial.omapiId;
      this.modalConfig = new ModalConfig();
      this.navigationState = Object.assign({}, this.navigationService.state);
      this.previousPage = this.entryComponent;
      this.modalConfig.showTrialPending = true;
      this.error = value.error;
      this.registerComponent(PageNames.TRIAL_ACTIVATION_PENDING);
    }
  }

  closeConditions(): void {
    this.resetModalConfig(PageNames.TRIAL_CODE_MODAL, this.navigationState);
    this.showTrialActivationModal();
  }

  public showTrialActivationModal(backValue?: any): void {
    this.modalConfig = new ModalConfig();
    this.modalConfig.showTrialCode = true;
    if (!backValue) {
      this.navigationState = Object.assign({}, this.navigationService.state);
    }
    if (this.previousPage === PageNames.TRIALS_PAGE || this.previousPage === PageNames.PLAY_SPORTS_PAGE) {
      this.previousPageState = this.navigationState;
    }
    this.registerComponent(PageNames.TRIAL_CODE_MODAL);
  }

  public showTrialComparisonModal(): void {
    this.modalConfig = new ModalConfig();
    this.modalConfig.showTrialComparison = true;
    this.navigationState = Object.assign({}, this.navigationService.state);
    this.registerComponent(PageNames.TRIAL_COMPARISON_MODAL);
  }

  public showFindTrialModal(savePageState?: boolean): void {
    this.modalConfig = new ModalConfig();
    this.modalConfig.showFindTrialCode = true;
    this.navigationState = Object.assign({}, this.navigationService.state);
    if (savePageState) {
      this.previousPageState = this.navigationState;
    }
    this.registerComponent(PageNames.FIND_TRIAL_CODE_MODAL);
  }

  public showTrialSuccessModal(): void {
    this.modalConfig = new ModalConfig();
    this.modalConfig.showTrialSuccess = true;
    this.navigationState = Object.assign({}, this.navigationService.state);
    this.registerComponent(PageNames.TRIAL_SUCCESS_MODAL);
  }

  private showTrialMaxAttemptsReached(): void {
    this.modalConfig = new ModalConfig();
    this.modalConfig.showTrialAttemptsReached = true;
    this.navigationState = Object.assign({}, this.navigationService.state);
    this.registerComponent(PageNames.TRIAL_ATTEMPTS_REACHED);
  }

  private registerComponent(pageName: PageNames) {
    this.navigationService.clearNavigation();
    this.navigationService.setCurrentPageComponent(pageName);
  }

  private resetModalConfig(pageName: any, state: NavigationState) {
    this.modalConfig = new ModalConfig();
    this.registerComponent(pageName);
    this.navigationService.state = state;
    this.ngOnDestroy();
  }

  public closeEventSubscriptions() {
    this.eventSubscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  closeModal(): void {
    this.modalConfig = new ModalConfig();
    this.registerComponent(PageNames.TRIAL_CODE_MODAL);
    if (!this.modalConfig.showOrderConditions && !this.modalConfig.showOrderPincode) {
      this.navigationService.state = this.navigationState;
    }
    this.ngOnDestroy();
  }

  public onBack(): void {
    this.closeModal();
  }
}
